import React, { useState } from 'react';

import { css } from '@emotion/css';
import { RecommendedMinMaxSource } from '@recurrency/core-api-schema/dist/common/enums';
import { Divider } from 'antd';
import { theme } from 'theme';

import { FlexSpace } from 'components/FlexSpace';
import { BorderedPropertyItem } from 'components/recipes/equation/BorderedPropertyItem';
import { EquationPopover } from 'components/recipes/equation/EquationPopover';
import { DemandPattern } from 'components/Tooltip/DemandPatternTooltip';
import { Typography } from 'components/Typography';

import { formatNumber, pluralize } from 'utils/formatting';
import { isMaxShelfLifeEnabled } from 'utils/tenantSettings';

import { EOQFormulaMinEquation, EOQFormulaMinEquationData } from './equations/EOQFormulaMinEquation';
import { EquationSymbol } from './equations/EquationSymbol';
import { RuleOverrideEquation } from './equations/RuleOverrideEquation';
import { SparseReplenishmentEquation, SparseReplenishmentEquationData } from './equations/SparseReplenishmentEquation';

export interface RecommendedMinData extends SparseReplenishmentEquationData, EOQFormulaMinEquationData {
  demand_pattern?: string;
  recommended_min_source?: string;
  max_shelf_life_days?: number | null;
  max_shelf_life_stock_cap?: number | null;
  shelf_life_capped?: boolean;
}

export const RecommendedMinPopover = ({
  record,
  showOpOqVariant = false,
  openSparseItemGrid,
}: {
  record: RecommendedMinData;
  showOpOqVariant?: boolean;
  openSparseItemGrid?: () => void;
}) => {
  const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);
  const minLabel = showOpOqVariant ? 'Order Point' : 'Min';
  const showMinStockFloorLogic = !!(record.min_stock_floor && record.recommended_min_prefloor < record.min_stock_floor);
  // Min after stock floor (if any), before shelf-life clamp.
  const preShelfLifeMin = showMinStockFloorLogic ? (record.min_stock_floor as number) : record.recommended_min_prefloor;
  const maxShelfLifeEnabled = isMaxShelfLifeEnabled();
  const showShelfLifeMinClamp =
    maxShelfLifeEnabled &&
    !!record.shelf_life_capped &&
    record.max_shelf_life_days != null &&
    record.max_shelf_life_stock_cap != null &&
    record.recommended_min < preShelfLifeMin;
  // This is to close the popover when the modal is opened from this popover
  const onClick = () => {
    setIsOpen(false);
    if (openSparseItemGrid) openSparseItemGrid();
  };
  // This is to reset the visibility to undefined so it can be opened on hover after it is closed
  const onVisibleChange = (isVisible: boolean) => {
    const visibility = isVisible === false ? undefined : isVisible;
    setIsOpen(visibility);
  };
  return (
    <EquationPopover title={`Recommended ${minLabel} Calculation`} visible={isOpen} onVisibleChange={onVisibleChange}>
      <FlexSpace
        direction="column"
        align-items="start"
        className={css`
          align-content: flex-start;
        `}
      >
        {record.recommended_min_source === RecommendedMinMaxSource.Automated ? (
          <RuleOverrideEquation
            type={`Recommended ${minLabel}`}
            value={record.recommended_min_prefloor}
            uom={record.unit_of_measure}
          />
        ) : record.demand_pattern === DemandPattern.SPARSE ? (
          <SparseReplenishmentEquation
            record={record}
            showOpOqVariant={showOpOqVariant}
            openSparseItemGrid={openSparseItemGrid ? onClick : undefined}
          />
        ) : (
          <EOQFormulaMinEquation record={record} showOpOqVariant={showOpOqVariant} />
        )}
        {showMinStockFloorLogic && (
          <>
            <Divider
              className={css`
                padding: 0px;
                margin: 0px;
              `}
            />
            <FlexSpace direction="row">
              <BorderedPropertyItem
                label={`Recommended ${minLabel}`}
                value={`${formatNumber(record.recommended_min_prefloor)} ${record.unit_of_measure}`}
              />
              <EquationSymbol character="<" />
              <BorderedPropertyItem
                label="Stock Floor"
                value={`${formatNumber(record.min_stock_floor)} ${record.unit_of_measure}`}
              />
            </FlexSpace>
            Because the recommendation is below the stock floor, the stock floor is used instead.
            <br />
          </>
        )}
        {showShelfLifeMinClamp && (
          <>
            <Divider
              className={css`
                padding: 0px;
                margin: 0px;
              `}
            />
            <FlexSpace direction="row">
              <BorderedPropertyItem
                label={`Recommended ${minLabel}`}
                value={`${formatNumber(preShelfLifeMin)} ${record.unit_of_measure}`}
              />
              <EquationSymbol character="→" />
              <BorderedPropertyItem
                label="Max Shelf Life Cap"
                value={`${formatNumber(record.max_shelf_life_stock_cap)} ${record.unit_of_measure}`}
              />
              <EquationSymbol character="=" />
              <BorderedPropertyItem
                label={`Recommended ${minLabel}`}
                value={`${formatNumber(record.recommended_min)} ${record.unit_of_measure}`}
              />
            </FlexSpace>
            <Typography type="small" style={{ color: theme.colors.primary[500] }}>
              Recommended {minLabel.toLowerCase()} capped so it does not exceed max shelf life of{' '}
              {pluralize(record.max_shelf_life_days ?? 0, 'day', 'days', true)} (round(
              {formatNumber(record.max_shelf_life_days)} × {formatNumber(record.avg_demand)}) ={' '}
              {formatNumber(record.max_shelf_life_stock_cap)} {record.unit_of_measure}).
            </Typography>
          </>
        )}
      </FlexSpace>
    </EquationPopover>
  );
};
